import React, { useState, useRef } from 'react'
import { path } from 'ramda'
import classNames from 'classnames'
import { useHistory } from 'react-router-dom'
import {
  displayPriceForSubscription,
  subStatusDisplay,
  getSubscriptionDisplayName,
  isDeviceLevelSubscription,
  plusSubHasCode,
  getUpgradeDowngradeAction,
  awnPlusAnnualMonthlySwitchAvail,
  isCameraSubscription,
  isHeatStressSubscription,
  getSubscriptionTypeClasses,
  getSubscriptionConfig,
  subIsIap,
  subIsIos,
  displayCancelPlusOnIosMessage
} from './index'
import { getDeviceLabel } from '../../common/ambient'
import AutoRenewToggle from './AutoRenewToggle'
import SmsAutoRefillToggle from './SmsAutoRefillToggle'
import UpgradeDowngradeLink from './UpgradeDowngradeLink'
import StayOnPlanButton from './StayOnPlanButton'
import './SubscriptionCard.less'

export default function SubscriptionCard ({ sub, user, device, userActions, index, isFreeCard, freeCardType = 'plus', canShowExternalPurchaseLinks = true }) {
  const history = useHistory()

  // Get device info if this is a device-level subscription
  const devices = path(['devices'], device) || []
  const deviceForSub = !isFreeCard && isDeviceLevelSubscription(sub)
    ? devices.find(d => d.macAddress === sub.macAddress)
    : null

  // Build class names for subscription card
  const cardClasses = classNames(
    {
      iap: subIsIap(sub)
    },
    'subscription-card',
    isFreeCard
      ? { 'free': true, 'heat-stress': freeCardType === 'heat-stress' }
      : getSubscriptionTypeClasses(sub)
  )

  // Check subscription type
  const isPlusType = (isFreeCard && freeCardType === 'plus') || /^awn-plus-/.test(sub?.productName)
  const isCameraType = !isFreeCard && isCameraSubscription(sub?.productName)
  const isHeatStressType = (isFreeCard && freeCardType === 'heat-stress') || isHeatStressSubscription(sub?.productName)

  // Calculate total SMS available
  const smsBank = path(['info', 'smsBank'], user) || 0
  const smsBankMetered = path(['info', 'smsBankMetered'], user) || 0
  const smsBankMeteredHs = path(['info', 'smsBankMeteredHs'], user) || 0
  const totalSms = smsBank + smsBankMetered
  const smsToDisplay = isHeatStressType ? smsBankMeteredHs : totalSms

  // Check if user already has a gift code subscription
  const subscribedWithCode = !isFreeCard && plusSubHasCode(user)

  // Check if upgrade is available
  const upgradeDowngradeAction = !isFreeCard && getUpgradeDowngradeAction(sub)
  const canUpgrade = isFreeCard || upgradeDowngradeAction === 'upgrade'

  // Check if change plan is available
  const canChangePlan = !isFreeCard && (isPlusType ? awnPlusAnnualMonthlySwitchAvail(user, sub) : (isCameraType || isHeatStressType)) && !sub?.willDowngradeTo

  // Check if this is an Apple IAP subscription that needs to be canceled
  const needsAppleCancellation = !isFreeCard && subIsIos(sub) && displayCancelPlusOnIosMessage(user)

  // Handle change plan click
  const handleChangePlan = () => {
    if (isPlusType) {
      history.push('/subscriptions/subscribe?type=awn-plus&changePlan=true')
    } else if (isHeatStressType) {
      history.push('/subscriptions/subscribe?type=heat-stress')
    } else if (isCameraType) {
      const currentDevice = devices.find(d => d.macAddress === sub.macAddress)
      if (currentDevice) {
        // Navigate to camera subscription page with device context
        history.push(`/subscriptions/subscribe?type=camera&deviceId=${currentDevice._id}`)
      }
    }
  }

  // Handle upgrade button click for free card
  const handleUpgrade = () => {
    if (isHeatStressType) {
      history.push('/subscriptions/subscribe?type=heat-stress')
    } else {
      history.push('/subscriptions/subscribe')
    }
  }

  // Plus features for the free card
  const plusFeatures = [
    'SMS Alerts',
    'Advanced map layers and map filtering',
    'Advanced graphing',
    'Bonus dashboard tiles',
    'Extended forecast - up to 15 days',
    'Longer data history - up to 5 years',
    'Team alerts'
  ]

  // Heat Stress features for the free card
  const heatStressFeatures = [
    'Up to 3000 SMS alerts per month',
    'Send alerts up to 15 people',
    'Auto top-ups to make sure critical safety SMS alerts never stop'
  ]

  return (
      <div className={cardClasses}>
        <div className='card-header'>
          {isFreeCard
            ? (isHeatStressType ? 'Heat Stress Free' : 'AWN Standard Free')
            : getSubscriptionDisplayName(sub)
          }
        </div>
        {isFreeCard && (
          <div className='card-subtitle'>
            {isHeatStressType ? 'Basic alerts at no cost' : 'Essential features at no cost'}
          </div>
        )}
        <div className='card-body'>
          {!isFreeCard && sub?.willDowngradeTo && (
            <div className='status-row downgrade-scheduled'>
              <div className='downgrade-message'>
                <div className='downgrade-scheduled-text'>
                  Downgrade to {getSubscriptionConfig(sub.willDowngradeTo)?.displayName || 'new plan'} scheduled
                </div>
                <div className='stay-message'>
                  Stay on {getSubscriptionConfig(sub.productName)?.displayName || 'current plan'} and keep your top tier features
                </div>
              </div>
            </div>
          )}
          {!isFreeCard && !sub?.willDowngradeTo && (
            needsAppleCancellation ? (
              <div className='status-row action-required'>
                <div className='status warning'>
                  <span className='warning-icon'>⚠</span> Action required
                </div>
              </div>
            ) : (
              <div className='status-row'>
                <div className='status'>{subStatusDisplay(sub)}</div>
                <div className='autorenew'>
                  <span>Auto-renew</span>
                  <AutoRenewToggle
                    sub={sub}
                    user={user}
                    userActions={userActions}
                  />
                </div>
              </div>
            )
          )}
          {(isPlusType || isHeatStressType) && (
            needsAppleCancellation ? (
              <div className='apple-cancellation-warning'>
                <div className='warning-message'>
                  Cancel Apple subscription to avoid double charge
                </div>
                <a
                  href='https://apps.apple.com/account/subscriptions'
                  target='_blank'
                  rel='noreferrer'
                  className='btn btn-primary'
                >
                  Cancel from App Store
                </a>
              </div>
            ) : (
              <div className='subscription-actions'>
                {canShowExternalPurchaseLinks && isPlusType && !subscribedWithCode && (
                  <div className='gift-code-button-section'>
                    <button
                      className='btn-gift-code'
                      onClick={() => userActions.doModal({ type: 'gift-code' })}
                    >
                      Redeem gift code
                    </button>
                  </div>
                )}

                {!isFreeCard && sub?.willDowngradeTo && (
                  <div className='stay-on-plan-section'>
                    <StayOnPlanButton
                      sub={sub}
                      user={user}
                    />
                  </div>
                )}

                {canUpgrade && !sub?.willDowngradeTo && (
                  <div className='upgrade-button-section'>
                    {isFreeCard ? (
                      <a onClick={handleUpgrade}>
                        Upgrade plan
                      </a>
                    ) : (
                      <UpgradeDowngradeLink
                        sub={sub}
                        user={user}
                        userActions={userActions}
                      />
                    )}
                  </div>
                )}
              </div>
            )
          )}
          {isFreeCard && (
            <div className='features-list'>
              <h4>
                {isHeatStressType
                  ? 'Upgrade your Heat Stress plan and get:'
                  : 'Upgrade to AWN+ plans and get:'
                }
              </h4>
              <ul>
                {(isHeatStressType ? heatStressFeatures : plusFeatures).map((feature, idx) => (
                  <li key={idx}>
                    <span className='checkmark'></span> {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!isFreeCard && !needsAppleCancellation && (
            <div className='subscription-meta'>
            {deviceForSub && (
              <div className='meta-row device-info'>
                <span className='label'>Device:</span>
                <span className='value'>{getDeviceLabel(deviceForSub)}</span>
              </div>
            )}
            <div className='meta-row renews'>
              <span className='label'>
                {sub?.willDowngradeTo ? 'Downgrades on:' : (sub?.cancelAtPeriodEnd ? 'Expires:' : 'Renews:')}
              </span>
              <span className='value'>{moment(sub.currentPeriodEnd * 1000).format('MMM D, YYYY')}</span>
            </div>
            <div className='meta-row renewal-price'>
              <span className='label'>Renewal Price:</span>
              <span className='value'>{displayPriceForSubscription(sub)}</span>
            </div>
            {(isPlusType || isHeatStressType) && (
              <div className='meta-row sms-alerts'>
                <span className='label'>Available SMS alerts:</span>
                <span className='value'>{smsToDisplay} SMS left</span>
              </div>
            )}
          </div>
          )}

          {!isFreeCard && isPlusType && !needsAppleCancellation && (
            <div className='sms-refill-section'>
              <div className='sms-refill-toggle'>
                <span className='sms-refill-label'>SMS auto refill</span>
                <SmsAutoRefillToggle user={user} userActions={userActions} />
              </div>
              <p className='sms-refill-disclaimer'>
                $1.50 per 100 SMS (Billed monthly if used)
              </p>
            </div>
          )}

          {!isFreeCard && isHeatStressType && !needsAppleCancellation && (
            <div className='sms-refill-section'>
              <h4>Alert Status</h4>
              <div className='meta-row'>
                <span className='label'>Auto Refilled:</span>
                <span className='value'>No SMS added</span>
              </div>
              <div className='meta-row'>
                <span className='label'>Additional charges:</span>
                <span className='value'>No Charges</span>
              </div>
            </div>
          )}

          {canChangePlan && (
            <div className='change-plan-section'>
              <a className='change-plan' onClick={handleChangePlan}>
                Change plan
              </a>
            </div>
          )}
        </div>
      </div>
    )
}

SubscriptionCard.displayName = 'SubscriptionCard'
